import app from 'durandal/app';
import * as ko from 'knockout';

import OwnershipInformationService from '@/legacy/components/user/onboardingWizard/company/ownershipInformation/services/ownershipInformationService';
import { Instance as companyService } from '@/legacy/services/company';
import { Instance as userService } from '@/legacy/services/user';
import {
  Instance as verificationService,
  Status,
} from '@/legacy/services/verification/verification';
import { Instance as verificationCompanyService } from '@/legacy/services/verification/verificationCompany';
import { Instance as verificationUserService } from '@/legacy/services/verification/verificationUser';
import {
  combineResults,
  mapResultObservable,
  observableToAsyncResult,
  Result,
} from '@/utils/result';

import { backendStatusToProgressJumio, backendStatusToProgressPersona } from './utils/utils';
import { VerificationProgress } from './types';

export const additionalVerificationItems = observableToAsyncResult(
  companyService.CompanyId,
  async (companyId) => {
    if (!companyId) {
      return Promise.reject(new Error('No company ID'));
    }
    return verificationService.getOutstandingVerificationItems(companyId);
  }
);

/** Backing field for {@see bankAccountVerificationInProgress} */
const bankAccountVerificationInProgressHack = ko.observableArray<string>();

/**
 * Adding a bank account doesn't trigger any app invalidations. SignalR doesn't fire a company
 * updated event. We do some hacking to pretend that all that stuff worked, similar to identity
 * verification in X1. We handle company changes by storing the companyId versus just a straight
 * boolean value globally for the app session.
 */
export const bankAccountVerificationInProgress = ko.pureComputed<boolean>({
  deferEvaluation: true,
  read: () =>
    bankAccountVerificationInProgressHack.indexOf(companyService.CompanyId()) >= 0 ||
    companyService.profile()?.bankAdded(),
  write: (value) => {
    if (!value) {
      bankAccountVerificationInProgressHack.remove(companyService.CompanyId());
    } else if (bankAccountVerificationInProgressHack.indexOf(companyService.CompanyId()) < 0) {
      bankAccountVerificationInProgressHack.push(companyService.CompanyId());
    }
  },
});

export const bankAccountVerificationCompleted = ko.pureComputed<boolean>({
  deferEvaluation: true,
  read: () => companyService.profile()?.isAnyBankAccountVerified(),
});

export const bankAccountVerificationProgress = ko.pureComputed({
  deferEvaluation: true,
  read: (): VerificationProgress => {
    if (bankAccountVerificationCompleted()) {
      return 'completed';
    }
    if (bankAccountVerificationInProgress()) {
      return 'in-progress';
    }
    return 'not-started';
  },
});

export const bankAccountVerificationNotStarted = ko.pureComputed({
  deferEvaluation: true,
  read: () => bankAccountVerificationProgress() === 'not-started',
});

export const verification = observableToAsyncResult(
  companyService.CompanyId,
  async (companyId: string) => {
    if (!companyId) {
      return Promise.reject(new Error('No company ID'));
    }
    return verificationService.getOnboardingWizardStatus(companyId);
  }
);

export const companyVerification = observableToAsyncResult(
  companyService.CompanyId,
  async (companyId) => {
    if (!companyId) {
      return Promise.reject(new Error('No company ID'));
    }
    return verificationCompanyService.GetVerifyCompanyStatus(companyId);
  }
);

const companyVerificationStatusHack = ko.observable<Result<Status> | undefined>(undefined);

export const companyVerificationStatus = combineResults(
  [verification, companyVerificationStatusHack] as const,
  (verificationDto, hack) => {
    if (hack) {
      return hack;
    }
    return verificationDto?.companyVerificationStatus;
  },
  { write: companyVerificationStatusHack }
);

export const companyVerificationProgress = mapResultObservable(
  companyVerificationStatus,
  (companyStatus) => {
    if (!companyStatus) {
      return 'not-started';
    }
    return companyService.profile()?.isSubjectToPersonaOnboarding()
      ? backendStatusToProgressPersona(companyStatus)
      : backendStatusToProgressJumio(companyStatus);
  }
);

export const companyVerificationCompleted = mapResultObservable(
  companyVerificationProgress,
  (companyProgress) => companyProgress === 'completed'
);

export const companyVerificationInProgress = mapResultObservable(
  companyVerificationProgress,
  (companyProgress) => companyProgress === 'in-progress'
);

export const companyVerificationNotStarted = mapResultObservable(
  companyVerificationProgress,
  (companyProgress) => companyProgress === 'not-started'
);

export const companyVerificationFailed = mapResultObservable(
  companyVerificationProgress,
  (companyProgress) => companyProgress === 'rejected'
);

export const ownershipInformation = observableToAsyncResult(
  companyService.CompanyId,
  async (companyId) => {
    if (!companyId) {
      return Promise.reject(new Error('No company ID'));
    }
    return OwnershipInformationService.get();
  },
  {
    errorValue: [],
    initialValue: [],
    pendingValue: [],
  }
);

const companyAndUserId = ko.computed(() => [companyService.CompanyId(), userService.UserId()]);

export const userVerification = observableToAsyncResult(
  companyAndUserId,
  async ([companyId, userId]) => {
    if (!companyId) {
      return Promise.reject(new Error('No company ID'));
    }
    if (!userId) {
      return Promise.reject(new Error('No user ID'));
    }
    return verificationUserService.GetVerifyIdentityStatus(companyId, userId);
  }
);

/**
 * Identity verification doesn't trigger any app invalidations. SignalR doesn't fire a user updated
 * event. We do some hacking to pretend that all that stuff worked.
 * @returns `null` when loading, `boolean` for status indicator.
 */
export const userVerificationCompleted = combineResults(
  [userVerification],
  (userVerificationDto) => userVerificationDto?.isIdentityVerified
);

const userVerificationProgressBackend = mapResultObservable(
  userVerification,
  (userVerificationDto) => {
    if (!userVerificationDto) {
      return 'not-started';
    }
    return companyService.profile()?.isSubjectToPersonaOnboarding()
      ? backendStatusToProgressPersona(userVerificationDto.status)
      : backendStatusToProgressJumio(userVerificationDto.status);
  }
);

/** Backing field for {@see userVerificationProgress} */
const userVerificationProgressHack = ko.observable<Result<VerificationProgress> | undefined>(
  undefined
);

export const userVerificationProgress = combineResults(
  [userVerificationProgressBackend, userVerificationProgressHack] as const,
  (backend, hack) => {
    if (hack) {
      return hack;
    }
    return backend;
  },
  {
    write: userVerificationProgressHack,
  }
);

export function userVerificationInProgressCombiner(
  progress: VerificationProgress,
  status: { isIdentitySubmitted: boolean }
): boolean {
  return progress === 'in-progress' || status?.isIdentitySubmitted;
}

/**
 * Identity verification doesn't trigger any app invalidations. SignalR doesn't fire a user updated
 * event. We do some hacking to pretend that all that stuff worked.
 */
export const userVerificationInProgress = combineResults(
  [userVerificationProgress, userVerification] as const,
  userVerificationInProgressCombiner
);

export const userVerificationNotStarted = mapResultObservable(
  userVerificationProgress,
  (userProgress) => userProgress === 'not-started'
);

export const userVerificationFailed = mapResultObservable(
  userVerificationProgress,
  (userProgress) => userProgress === 'rejected'
);

export const userVerificationStatus = mapResultObservable(userVerification, (data) => data.status);

export function verificationCompletedCombiner(
  company: boolean,
  identity: boolean,
  unable: boolean
): boolean {
  return company && identity && !unable;
}

export const verificationCompleted = combineResults(
  [companyVerificationCompleted, userVerificationCompleted, userVerificationFailed],
  verificationCompletedCombiner
);

export function verificationInProgressCombiner(company: boolean, identity: boolean): boolean {
  return company || identity || bankAccountVerificationInProgress();
}

export const verificationInProgress = combineResults(
  [companyVerificationInProgress, userVerificationInProgress],
  verificationInProgressCombiner
);

export function verificationFailedCombiner(company: boolean, identity: boolean): boolean {
  return company || identity;
}

export const verificationFailed = combineResults(
  [companyVerificationFailed, userVerificationFailed],
  verificationFailedCombiner
);

/** Backing field for {@see testPaymentMade} */
const testPaymentMadeHack = ko.observableArray<string>();

/**
 * Creating a payable doesn't trigger any app invalidations. SignalR doesn't fire a company updated
 * event. We do some hacking to pretend that all that stuff worked, similar to identity verification
 * in X1. We handle company changes by storing the companyId versus just a straight boolean value
 * globally for the app session.
 */
export const testPaymentMade = ko.pureComputed<boolean>({
  deferEvaluation: true,
  read: () =>
    testPaymentMadeHack.indexOf(companyService.CompanyId()) >= 0 ||
    companyService.profile()?.paymentAdded(),
  write: (value) => {
    if (!value) {
      testPaymentMadeHack.remove(companyService.CompanyId());
    } else if (testPaymentMadeHack.indexOf(companyService.CompanyId()) < 0) {
      testPaymentMadeHack.push(companyService.CompanyId());
    }
  },
});

export const allVerificationResults = combineResults(
  [
    additionalVerificationItems,
    companyVerification,
    companyVerificationCompleted,
    companyVerificationInProgress,
    companyVerificationNotStarted,
    companyVerificationProgress,
    companyVerificationStatus,
    ownershipInformation,
    userVerification,
    userVerificationCompleted,
    userVerificationInProgress,
    userVerificationNotStarted,
    userVerificationProgress,
    userVerificationStatus,
    verification,
    verificationCompleted,
    verificationFailed,
    verificationInProgress,
  ] as const,
  (
    additionalVerificationItemsData,
    companyVerificationData,
    companyVerificationCompletedData,
    companyVerificationInProgressData,
    companyVerificationNotStartedData,
    companyVerificationProgressData,
    companyVerificationStatusData,
    ownershipInformationData,
    userVerificationData,
    userVerificationCompletedData,
    userVerificationInProgressData,
    userVerificationNotStartedData,
    userVerificationProgressData,
    userVerificationStatusData,
    verificationData,
    verificationCompletedData,
    verificationFailedData,
    verificationInProgressData
  ) => ({
    additionalVerificationItems: additionalVerificationItemsData,
    companyVerification: companyVerificationData,
    companyVerificationCompleted: companyVerificationCompletedData,
    companyVerificationInProgress: companyVerificationInProgressData,
    companyVerificationNotStarted: companyVerificationNotStartedData,
    companyVerificationProgress: companyVerificationProgressData,
    companyVerificationStatus: companyVerificationStatusData,
    ownershipInformation: ownershipInformationData,
    userVerification: userVerificationData,
    userVerificationCompleted: userVerificationCompletedData,
    userVerificationInProgress: userVerificationInProgressData,
    userVerificationNotStarted: userVerificationNotStartedData,
    userVerificationProgress: userVerificationProgressData,
    userVerificationStatus: userVerificationStatusData,
    verification: verificationData,
    verificationCompleted: verificationCompletedData,
    verificationFailed: verificationFailedData,
    verificationInProgress: verificationInProgressData,
  })
);

app.on('onboarding:identity:updated').then(() => {
  userVerificationProgressHack({ data: 'in-progress', error: null, isLoading: false });
});
app.on('onboarding:account:updated').then(() => {
  verificationService.getOutstandingVerificationItems(companyService.CompanyId()).then((items) => {
    additionalVerificationItems({ data: items, error: null, isLoading: false });
  });
  companyService.LoadCompanyProfile(companyService.CompanyId());
});
